import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Play, ArrowLeft, Gamepad2, Target, Star, Zap } from 'lucide-react'

export default function OceanDashInfoPage() {
  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      {/* Back Button */}
      <Link to="/" className="inline-flex items-center text-dark-navy/70 hover:text-dark-navy mb-6 transition-colors">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Games
      </Link>

      {/* Hero Section */}
      <div className="text-center mb-12">
        <div className="text-6xl mb-4">🧜‍♀️</div>
        <h1 className="text-4xl md:text-5xl font-bold text-dark-navy mb-4">Ocean Dash</h1>
        <p className="text-xl text-dark-navy/70 max-w-2xl mx-auto">
          An exciting endless swimming adventure! Help Thea navigate through the beautiful ocean, 
          collect treasures, and avoid obstacles.
        </p>
        
        {/* Play Button */}
        <div className="mt-8">
          <Link to="/ocean-dash/play">
            <Button size="lg" className="text-lg px-8 py-6 rounded-full shadow-lg hover:shadow-xl transition-all bg-bright-coral hover:bg-sandy-coral text-white">
              <Play className="w-5 h-5 mr-2" />
              Start Playing
            </Button>
          </Link>
        </div>
      </div>

      {/* Screenshot Section */}
      <div className="mb-12">
        <div className="bg-gradient-to-br from-dark-navy/90 to-medium-teal/80 rounded-2xl overflow-hidden shadow-xl aspect-video flex items-center justify-center">
          <img 
            src="/screenshots/ocean_dash.jpg" 
            alt="Ocean Dash gameplay screenshot"
            className="w-full h-full object-cover"
            onError={(e) => {
              // Hide image and show placeholder if not found
              e.currentTarget.style.display = 'none'
              e.currentTarget.nextElementSibling?.classList.remove('hidden')
            }}
          />
          <div className="hidden text-center p-8">
            <Gamepad2 className="w-16 h-16 text-white/50 mx-auto mb-4" />
            <p className="text-white/70">Screenshot coming soon!</p>
          </div>
        </div>
      </div>

      {/* Quick Start Guide */}
      <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 md:p-8 mb-8 border border-white/40">
        <h2 className="text-2xl font-bold text-dark-navy mb-6 flex items-center gap-2">
          <Target className="w-6 h-6 text-bright-coral" />
          How to Play
        </h2>
        
        <div className="grid md:grid-cols-2 gap-4">
          <div className="flex items-start gap-3 p-4 bg-white/50 rounded-xl">
            <span className="text-2xl">🐠</span>
            <div>
              <h3 className="font-semibold text-dark-navy">Tap or Click</h3>
              <p className="text-dark-navy/70 text-sm">Tap the screen, click, or press SPACE to swim upward</p>
            </div>
          </div>
          
          <div className="flex items-start gap-3 p-4 bg-white/50 rounded-xl">
            <span className="text-2xl">⬇️</span>
            <div>
              <h3 className="font-semibold text-dark-navy">Release to Drift</h3>
              <p className="text-dark-navy/70 text-sm">Let go and Thea will gently drift down with the current</p>
            </div>
          </div>
          
          <div className="flex items-start gap-3 p-4 bg-white/50 rounded-xl">
            <span className="text-2xl">🐚</span>
            <div>
              <h3 className="font-semibold text-dark-navy">Collect Pearls</h3>
              <p className="text-dark-navy/70 text-sm">Gather pearls to earn currency for rewards</p>
            </div>
          </div>
          
          <div className="flex items-start gap-3 p-4 bg-white/50 rounded-xl">
            <span className="text-2xl">🪼</span>
            <div>
              <h3 className="font-semibold text-dark-navy">Avoid Obstacles</h3>
              <p className="text-dark-navy/70 text-sm">Watch out for jellyfish, seaweed, and coral!</p>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Guide */}
      <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 md:p-8 mb-8 border border-white/40">
        <h2 className="text-2xl font-bold text-dark-navy mb-6 flex items-center gap-2">
          <Star className="w-6 h-6 text-medium-teal" />
          Tips & Power-ups
        </h2>
        
        <div className="space-y-6">
          <div>
            <h3 className="font-semibold text-dark-navy text-lg mb-3">Collectibles</h3>
            <ul className="space-y-2 text-dark-navy/80">
              <li className="flex items-center gap-2">
                <span className="text-xl">🐚</span>
                <span><strong>Golden Shells</strong> – Worth 5 pearls each! Keep an eye out for these rare treasures.</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-xl">💎</span>
                <span><strong>Regular Pearls</strong> – The main currency. Collect as many as you can!</span>
              </li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-semibold text-dark-navy text-lg mb-3">Power-ups</h3>
            <ul className="space-y-2 text-dark-navy/80">
              <li className="flex items-center gap-2">
                <span className="text-xl">🛡️</span>
                <span><strong>Bubble Shield</strong> – Gives you temporary invincibility. Perfect for tricky sections!</span>
              </li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-semibold text-dark-navy text-lg mb-3">Pro Tips</h3>
            <ul className="space-y-2 text-dark-navy/80">
              <li className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-bright-coral" />
                <span>The game gets faster the further you swim – stay focused!</span>
              </li>
              <li className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-bright-coral" />
                <span>Gentle, rhythmic taps work better than frantic clicking.</span>
              </li>
              <li className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-bright-coral" />
                <span>Try to stay in the middle of the screen for more reaction time.</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Difficulty Levels */}
      <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 md:p-8 mb-8 border border-white/40">
        <h2 className="text-2xl font-bold text-dark-navy mb-6">Difficulty Levels</h2>
        
        <div className="grid md:grid-cols-3 gap-4">
          <div className="p-4 bg-green-100/50 rounded-xl border-2 border-green-200">
            <h3 className="font-bold text-green-700 mb-2">🌱 Easy</h3>
            <p className="text-sm text-dark-navy/70">Perfect for beginners! Slower pace with fewer obstacles.</p>
          </div>
          
          <div className="p-4 bg-yellow-100/50 rounded-xl border-2 border-yellow-200">
            <h3 className="font-bold text-yellow-700 mb-2">🌿 Medium</h3>
            <p className="text-sm text-dark-navy/70">A balanced challenge with moderate speed and obstacles.</p>
          </div>
          
          <div className="p-4 bg-red-100/50 rounded-xl border-2 border-red-200">
            <h3 className="font-bold text-red-700 mb-2">🔥 Hard</h3>
            <p className="text-sm text-dark-navy/70">For expert swimmers! Fast pace with lots of obstacles.</p>
          </div>
        </div>
      </div>

      {/* Bottom CTA */}
      <div className="text-center py-8">
        <Link to="/ocean-dash/play">
          <Button size="lg" className="text-lg px-10 py-6 rounded-full shadow-lg hover:shadow-xl transition-all bg-bright-coral hover:bg-sandy-coral text-white">
            <Play className="w-5 h-5 mr-2" />
            Start Your Adventure!
          </Button>
        </Link>
      </div>
    </div>
  )
}
